import {
  postApiUserInstitutionIdUserIdRegenerateTempPassword,
  type UserCredentials,
} from '@smartsash/api-client';
import { openModal } from '@smartsash/ui';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { openCredentialsModal } from '@/ui/shared/UserCredentials';
import { getErrorMessage } from '@/utils/getErrorMessage';

/**
 * `POST /api/User/{institutionId}/{userId}/regenerate-temp-password` really answers
 * `{ username, temporaryPassword, requiresPasswordChange }`
 * (`UserController.RegenerateTemporaryPassword`), but it returns an anonymous object, so the
 * OpenAPI snapshot declares its `200` with no content schema and Orval types the fetcher
 * `Promise<void>`.
 *
 * Generated code is never hand-edited, so the shape is asserted here at runtime instead. A
 * response that does not match raises an error rather than opening a blank dialog. Declaring
 * the body in the backend's swagger — after which this schema and the cast below both go away —
 * is a recorded follow-up of HS-1224.
 */
const regeneratedCredentialsSchema = z.object({
  username: z.string().min(1),
  temporaryPassword: z.string().min(1),
  requiresPasswordChange: z.boolean().optional(),
});

/**
 * The same fetcher, re-typed to admit that it resolves with a body.
 *
 * Reading the value straight off the generated `Promise<void>` trips
 * `no-confusing-void-expression`, and rightly so — a `void` result used as a value is a bug
 * everywhere except here, where the declared type is the one that is wrong. Correcting it once,
 * next to the explanation above, keeps the call site honest.
 */
const requestRegeneratedCredentials =
  postApiUserInstitutionIdUserIdRegenerateTempPassword as unknown as (
    institutionId: string,
    userId: string,
  ) => Promise<unknown>;

export interface RegenerateTempPasswordTarget {
  institutionId: string;
  userId: string;
  /** Shown in the confirmation copy so the admin can see whose password they are resetting. */
  userName: string;
}

/**
 * Confirm, regenerate and display a user's temporary password (HS-1224).
 *
 * Both entry points — the Users row action and the user edit screen — call this, so the flow
 * exists once. The regenerated password lives only in the promise chain below and in the modal
 * it is handed to: it is never cached, persisted or logged (see `CredentialsPanel`).
 */
export function useRegenerateTempPassword(): (
  target: RegenerateTempPasswordTarget,
) => Promise<void> {
  const { t } = useTranslation();

  return useCallback(
    async ({ institutionId, userId, userName }: RegenerateTempPasswordTarget) => {
      const { action } = await openModal({
        title: t('users.regeneratePassword.confirmTitle', { name: userName }),
        description: t('users.regeneratePassword.confirmDescription'),
        variant: 'warning',
        actions: [
          { label: t('common.cancel'), value: 'cancel' },
          { label: t('users.regeneratePassword.confirm'), value: 'confirm' },
        ],
      }).closed;

      if (action !== 'confirm') return;

      let credentials: UserCredentials;
      try {
        const response = await requestRegeneratedCredentials(institutionId, userId);
        credentials = regeneratedCredentialsSchema.parse(response);
      } catch (error) {
        await openModal({
          title: t('users.regeneratePassword.error'),
          description: getErrorMessage(error, t('users.regeneratePassword.error'), t),
          variant: 'error',
          actions: [{ label: t('common.ok'), value: 'ok' }],
        }).closed;
        return;
      }

      await openCredentialsModal(credentials, t, t('users.regeneratePassword.resultTitle'));
    },
    [t],
  );
}
